import { useCallback, useMemo } from 'react';
import { useRecoilValueLoadable, useSetRecoilState } from 'recoil';

import { bidAwardeesQuerySelector, bidAwardeesRequestIdState } from './bid-awardees.recoil';
import { useRouter, useUpdateAwardeeAndNonAwardeeEmailInfo } from 'src/shared/hooks';
import { addAwardees } from 'src/store/services';
import { AwardeeDraft } from './bid-awardees';

/** hook for accessing bidAwardees from a single bidId. */
export function useBidAwardees(id?: number) {
  const { routerParams } = useRouter();
  const updateAwardeeAndNonAwardeeEmailInfo = useUpdateAwardeeAndNonAwardeeEmailInfo();
  const bidId = id || (routerParams.bidId && Number(routerParams.bidId)) || undefined;

  const setBidAwardeesRequestId = useSetRecoilState(bidAwardeesRequestIdState(bidId));
  const bidAwardeesLoadable = useRecoilValueLoadable(bidAwardeesQuerySelector(bidId));
  const bidAwardees = useMemo(() => {
    return bidAwardeesLoadable.valueMaybe() || [];
  }, [bidAwardeesLoadable]);

  /**
   * manually update bidAwardees of the bidId passed to useAwardees
   */
  const refreshBidAwardees = useCallback(() => {
    setBidAwardeesRequestId(requestId => requestId + 1);
  }, [setBidAwardeesRequestId]);

  /**  */
  const updateAwardees = useCallback(
    async (bidAwardees: AwardeeDraft[]) => {
      if (bidId) {
        await addAwardees({
          bidId,
          bidAwardees,
        });
        refreshBidAwardees();
      }
      /** Because this will only be called within the Award Bid Wizard,
       * this forces an update for email message info. (Specifically, the recipients will change)
       */
      updateAwardeeAndNonAwardeeEmailInfo();
      return;
    },
    [bidId, refreshBidAwardees, updateAwardeeAndNonAwardeeEmailInfo],
  );

  return {
    refreshBidAwardees,
    bidAwardees,
    bidAwardeesLoadable,
    updateAwardees,
  };
}
